import { useEffect, useRef, useState } from "react";
import {
  getAllPromoters, setTarget, getTarget, addBonusProgram, getAllBonusPrograms, getAllSKUs, addAuditLog,
  getPendingTargets, approveTarget, updateBonusProgram, revokeBonusProgram, restoreBonusProgram, getPayrollClosing,
} from "../../firebase/services";
import { format, parseISO, eachMonthOfInterval } from "date-fns";
import { useLanguage } from "../../hooks/useLanguage";

export default function AdminTargets() {
  const { t } = useLanguage();
  const [promoters, setPromoters] = useState([]);
  const [month, setMonth] = useState(format(new Date(), "yyyy-MM"));
  const [targetForm, setTargetForm] = useState({ promoterId: "", sellTarget: "", highendTarget: "" });
  const [programs, setPrograms] = useState([]);
  const EMPTY_PROG = { name: "", startDate: "", endDate: "", skus: [], amountPerUnit: "" };
  const [progForm, setProgForm] = useState(EMPTY_PROG);
  const [editingProg, setEditingProg] = useState(null);   // chương trình đang sửa (bản gốc)
  const [progBusy, setProgBusy] = useState(false);
  const progFormRef = useRef(null);
  const [skuList, setSkuList] = useState([]);
  const [skuFilter, setSkuFilter] = useState({ name: "", gpu: "" });
  const [msg, setMsg] = useState("");
  const [pendingTargets, setPendingTargets] = useState([]);
  const [approveVals, setApproveVals] = useState({}); // id → { sellTarget, highendTarget }

  const loadPending = () => getPendingTargets().then(list => {
    setPendingTargets(list);
    const vals = {};
    list.forEach(t => { vals[t.id] = { sellTarget: t.sellTarget, highendTarget: t.highendTarget }; });
    setApproveVals(vals);
  });

  useEffect(() => {
    getAllPromoters().then(p => {
      setPromoters(p);
      if (p.length) setTargetForm(f => ({ ...f, promoterId: p[0].uid }));
    });
    getAllBonusPrograms().then(setPrograms);
    getAllSKUs().then(skus => setSkuList(skus.sort((a, b) => (a.model || "").localeCompare(b.model || ""))));
    loadPending();
  }, []);

  const handleSetTarget = async (e) => {
    e.preventDefault();
    const p = promoters.find(x => x.uid === targetForm.promoterId);
    await setTarget({ promoterId: targetForm.promoterId, month, sellTarget: targetForm.sellTarget, highendTarget: targetForm.highendTarget, promoterName: p?.name || "" });
    setMsg(t("prog.saved_target"));
    setTimeout(() => setMsg(""), 2000);
  };

  const handleApproveTarget = async (tgt) => {
    const vals = approveVals[tgt.id] || { sellTarget: tgt.sellTarget, highendTarget: tgt.highendTarget };
    await approveTarget(tgt, vals, "Admin");
    setMsg(`Đã duyệt target của ${tgt.promoterName || "promoter"} ✓`);
    loadPending();
    setTimeout(() => setMsg(""), 3000);
  };

  const flash = (text, ms = 3000) => { setMsg(text); setTimeout(() => setMsg(""), ms); };

  // Các tháng đã chốt sổ mà chương trình có chạy — sửa / thu hồi sẽ làm đổi lương đã chốt
  const closedMonthsOf = async (...ranges) => {
    const months = new Set();
    ranges.filter(r => r?.startDate && r?.endDate && r.startDate <= r.endDate).forEach(r => {
      eachMonthOfInterval({ start: parseISO(r.startDate), end: parseISO(r.endDate) })
        .forEach(d => months.add(format(d, "yyyy-MM")));
    });
    const res = await Promise.all([...months].map(m => getPayrollClosing(m).then(c => (c?.closed ? m : null)).catch(() => null)));
    return res.filter(Boolean).sort();
  };
  const confirmClosed = async (...ranges) => {
    const closed = await closedMonthsOf(...ranges);
    return closed.length === 0 || window.confirm(t("prog.closed_warn", { months: closed.join(", ") }));
  };

  const handleAddProgram = async (e) => {
    e.preventDefault();
    if (progForm.skus.length === 0) { flash(t("prog.need_sku")); return; }
    if (progForm.endDate < progForm.startDate) { flash(t("prog.bad_dates")); return; }
    const data = { ...progForm, skus: progForm.skus, amountPerUnit: Number(progForm.amountPerUnit) };
    setProgBusy(true);
    try {
      if (editingProg) {
        if (!(await confirmClosed(editingProg, data))) { setProgBusy(false); return; }
        await updateBonusProgram(editingProg.id, data, editingProg, "Admin");
        flash(t("prog.updated_notified"));
        setEditingProg(null);
      } else {
        await addBonusProgram(data);
        addAuditLog({ type: "bonus_program_added", actorName: "Admin", actorRole: "admin", detail: `Tạo chương trình thưởng: "${progForm.name}" (${progForm.startDate} → ${progForm.endDate})`, meta: { name: progForm.name } });
        flash(t("prog.created_notified"));
      }
      setProgForm(EMPTY_PROG);
      getAllBonusPrograms().then(setPrograms);
    } catch (err) { flash("Lỗi: " + err.message, 5000); }
    setProgBusy(false);
  };

  const startEdit = (p) => {
    setEditingProg(p);
    setProgForm({
      name: p.name || "", startDate: p.startDate || "", endDate: p.endDate || "",
      skus: Array.isArray(p.skus) ? [...p.skus] : [], amountPerUnit: p.amountPerUnit ?? "",
    });
    progFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const cancelEdit = () => { setEditingProg(null); setProgForm(EMPTY_PROG); };

  const handleRevoke = async (p) => {
    const reason = window.prompt(t("prog.revoke_prompt", { name: p.name }));
    if (reason === null) return;                       // bấm Hủy
    if (!(await confirmClosed(p))) return;
    setProgBusy(true);
    try {
      await revokeBonusProgram(p, reason.trim(), "Admin");
      if (editingProg?.id === p.id) cancelEdit();
      flash(t("prog.revoked_notified"));
      getAllBonusPrograms().then(setPrograms);
    } catch (err) { flash("Lỗi: " + err.message, 5000); }
    setProgBusy(false);
  };

  const handleRestore = async (p) => {
    if (!window.confirm(t("prog.restore_confirm", { name: p.name }))) return;
    if (!(await confirmClosed(p))) return;
    setProgBusy(true);
    try {
      await restoreBonusProgram(p, "Admin");
      flash(t("prog.restored_notified"));
      getAllBonusPrograms().then(setPrograms);
    } catch (err) { flash("Lỗi: " + err.message, 5000); }
    setProgBusy(false);
  };

  const todayStr = format(new Date(), "yyyy-MM-dd");
  const isRevoked = (p) => p.status === "revoked";
  const activePrograms = programs.filter(p => !isRevoked(p) && (p.endDate || "") >= todayStr)
    .sort((a, b) => (a.startDate || "").localeCompare(b.startDate || ""));
  const endedPrograms = programs.filter(p => !isRevoked(p) && (p.endDate || "") < todayStr)
    .sort((a, b) => (b.endDate || "").localeCompare(a.endDate || ""));
  const revokedPrograms = programs.filter(isRevoked)
    .sort((a, b) => (b.endDate || "").localeCompare(a.endDate || ""));

  const progActions = (p) => (
    <td style={{ whiteSpace: "nowrap" }}>
      {isRevoked(p) ? (
        <button className="btn btn-sm" disabled={progBusy} onClick={() => handleRestore(p)}>♻️ {t("prog.restore")}</button>
      ) : (
        <>
          <button className="btn btn-sm" disabled={progBusy} onClick={() => startEdit(p)}>✏️ {t("prog.edit")}</button>{" "}
          <button className="btn btn-sm" disabled={progBusy} style={{ color: "#C8152A" }} onClick={() => handleRevoke(p)}>⛔ {t("prog.revoke")}</button>
        </>
      )}
    </td>
  );

  const gpuOptions = [...new Set(skuList.map(s => s.gpu).filter(Boolean))].sort();
  const filteredSkus = skuList.filter(sku => {
    if (sku.status === "discontinued" && !progForm.skus.includes(sku.model)) return false;
    const nameMatch = !skuFilter.name || (sku.model || "").toLowerCase().includes(skuFilter.name.toLowerCase());
    const gpuMatch = !skuFilter.gpu || sku.gpu === skuFilter.gpu;
    return nameMatch && gpuMatch;
  });

  const onSelectPromoter = async (uid) => {
    setTargetForm(f => ({ ...f, promoterId: uid }));
    const t = await getTarget(uid, month);
    if (t) setTargetForm(f => ({ ...f, sellTarget: t.sellTarget, highendTarget: t.highendTarget }));
  };

  return (
    <>
      <div className="page-header"><h1>{t("target.title")}</h1><p>{t("target.subtitle")}</p></div>
      {msg && <div className="alert alert-success">{msg}</div>}

      {pendingTargets.length > 0 && (
        <div className="card" style={{ marginBottom: 16, borderLeft: "4px solid #EF9F27" }}>
          <div className="card-title" style={{ marginBottom: 10 }}>{t("tga.pending_title")} ({pendingTargets.length})</div>
          <table className="table-card">
            <thead>
              <tr><th>{t("x.month")}</th><th>Promoter</th><th>{t("tga.proposed_by")}</th><th>{t("tga.ds_prop")}</th><th>{t("tga.he_prop")}</th><th>{t("tga.ds_final")}</th><th>{t("tga.he_final")}</th><th></th></tr>
            </thead>
            <tbody>
              {pendingTargets.map(tgt => {
                const v = approveVals[tgt.id] || {};
                const changed = Number(v.sellTarget) !== tgt.proposedSell || Number(v.highendTarget) !== tgt.proposedHighend;
                return (
                  <tr key={tgt.id}>
                    <td data-label="Tháng" style={{ fontSize: 12, color: "#888" }}>{tgt.month}</td>
                    <td data-label="Promoter"><strong>{tgt.promoterName || tgt.promoterId}</strong></td>
                    <td data-label="Đề xuất bởi" style={{ fontSize: 12 }}>{tgt.proposedByName || "—"}</td>
                    <td data-label="DS đề xuất">{tgt.proposedSell}</td>
                    <td data-label="HE đề xuất">{tgt.proposedHighend}</td>
                    <td data-label="DS duyệt">
                      <input type="number" value={v.sellTarget ?? ""} style={{ width: 70 }}
                        onChange={e => setApproveVals(a => ({ ...a, [tgt.id]: { ...a[tgt.id], sellTarget: e.target.value } }))} />
                    </td>
                    <td data-label="HE duyệt">
                      <input type="number" value={v.highendTarget ?? ""} style={{ width: 70 }}
                        onChange={e => setApproveVals(a => ({ ...a, [tgt.id]: { ...a[tgt.id], highendTarget: e.target.value } }))} />
                    </td>
                    <td data-label="">
                      <button className="btn btn-primary btn-sm" onClick={() => handleApproveTarget(tgt)}>
                        {t("tga.approve")}{changed ? " " + t("tga.adjusted") : ""}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{ fontSize: 11, color: "#888", marginTop: 8 }}>
            {t("tga.hint")}
          </div>
        </div>
      )}

      <div className="grid-2">
        <div className="card">
          <div className="card-title">{t("target.sell_target")}</div>
          <form onSubmit={handleSetTarget}>
            <div className="form-group">
              <label className="form-label">{t("sales.promoter")}</label>
              <select value={targetForm.promoterId} onChange={e => onSelectPromoter(e.target.value)}>
                {promoters.map(p => <option key={p.uid} value={p.uid}>{p.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">{t("common.month")}</label>
              <input type="month" value={month} onChange={e => setMonth(e.target.value)} />
            </div>
            <div className="grid-2" style={{ gap: 10 }}>
              <div className="form-group">
                <label className="form-label">{t("target.sell_target")}</label>
                <input type="number" value={targetForm.sellTarget} onChange={e => setTargetForm(f => ({ ...f, sellTarget: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">{t("target.he_target")}</label>
                <input type="number" value={targetForm.highendTarget} onChange={e => setTargetForm(f => ({ ...f, highendTarget: e.target.value }))} />
              </div>
            </div>
            <button className="btn btn-primary btn-full" type="submit">{t("common.save")}</button>
          </form>
        </div>
        <div className="card" ref={progFormRef} style={editingProg ? { outline: "2px solid #EF9F27" } : undefined}>
          <div className="card-title">{editingProg ? t("prog.edit_title", { name: editingProg.name }) : t("prog.add_title")}</div>
          {editingProg && <div className="alert alert-warn" style={{ fontSize: 12, marginBottom: 10 }}>{t("prog.edit_note")}</div>}
          <form onSubmit={handleAddProgram}>
            <div className="form-group">
              <label className="form-label">{t("prog.name")}</label>
              <input value={progForm.name} onChange={e => setProgForm(f => ({ ...f, name: e.target.value }))} required />
            </div>
            <div className="grid-2" style={{ gap: 10 }}>
              <div className="form-group">
                <label className="form-label">{t("prog.from")}</label>
                <input type="date" value={progForm.startDate} onChange={e => setProgForm(f => ({ ...f, startDate: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label className="form-label">{t("prog.to")}</label>
                <input type="date" value={progForm.endDate} onChange={e => setProgForm(f => ({ ...f, endDate: e.target.value }))} required />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                {t("prog.sku_apply")}{progForm.skus.length > 0 && <span style={{ color: "#C8152A", fontWeight: 500, marginLeft: 6 }}>({progForm.skus.length} {t("prog.selected")})</span>}
              </label>
              <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                <input
                  placeholder={t("prog.find_name")}
                  value={skuFilter.name}
                  onChange={e => setSkuFilter(f => ({ ...f, name: e.target.value }))}
                  style={{ flex: 1, fontSize: 13, padding: "5px 10px" }}
                />
                <select
                  value={skuFilter.gpu}
                  onChange={e => setSkuFilter(f => ({ ...f, gpu: e.target.value }))}
                  style={{ fontSize: 13, padding: "5px 8px" }}
                >
                  <option value="">{t("prog.all_gpu")}</option>
                  {gpuOptions.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
              <div style={{ maxHeight: 180, overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: 8, padding: "6px 10px" }}>
                {filteredSkus.map(sku => (
                  <label key={sku.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={progForm.skus.includes(sku.model)}
                      onChange={() => setProgForm(f => ({
                        ...f,
                        skus: f.skus.includes(sku.model)
                          ? f.skus.filter(s => s !== sku.model)
                          : [...f.skus, sku.model],
                      }))}
                    />
                    <span style={{ fontSize: 13 }}>{sku.model}</span>
                    {sku.gpu && <span style={{ fontSize: 11, color: "#888" }}>{sku.gpu}</span>}
                    {sku.partNo && <span style={{ fontSize: 11, color: "#aaa" }}>{sku.partNo}</span>}
                  </label>
                ))}
                {filteredSkus.length === 0 && <div style={{ color: "#aaa", fontSize: 13, padding: "4px 0" }}>{t("prog.no_sku")}</div>}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">{t("prog.per_unit")}</label>
              <input type="number" value={progForm.amountPerUnit} onChange={e => setProgForm(f => ({ ...f, amountPerUnit: e.target.value }))} required />
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn btn-primary" style={{ flex: 1 }} type="submit" disabled={progBusy}>
                {progBusy ? t("common.saving") : editingProg ? t("prog.save_edit") : t("prog.create")}
              </button>
              {editingProg && <button className="btn" type="button" disabled={progBusy} onClick={cancelEdit}>{t("common.cancel")}</button>}
            </div>
          </form>
        </div>
      </div>

      <div className="card">
        <div className="card-title">{t("prog.running")}</div>
        <table>
          <thead><tr><th>{t("prog.col_name")}</th><th>{t("prog.col_time")}</th><th>SKU</th><th>{t("prog.col_bonus")}</th><th></th></tr></thead>
          <tbody>
            {activePrograms.map(p => (
              <tr key={p.id} style={editingProg?.id === p.id ? { background: "#FFF7E6" } : undefined}>
                <td>
                  {p.name}
                  {p.startDate > todayStr && <span className="badge badge-blue" style={{ marginLeft: 6 }}>{t("bp.st_upcoming")}</span>}
                </td>
                <td style={{ fontSize: 12, color: "#888" }}>{p.startDate} → {p.endDate}</td>
                <td style={{ fontSize: 12 }}>{Array.isArray(p.skus) ? p.skus.join(", ") : p.skus}</td>
                <td>{Number(p.amountPerUnit).toLocaleString("vi-VN")} đ</td>
                {progActions(p)}
              </tr>
            ))}
            {activePrograms.length === 0 && (
              <tr><td colSpan={5} style={{ color: "#aaa", textAlign: "center", padding: 16 }}>{t("prog.none_running")}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {endedPrograms.length > 0 && (
        <div className="card">
          <div className="card-title" style={{ color: "#888" }}>{t("prog.ended")}</div>
          <table>
            <thead><tr><th>{t("prog.col_name")}</th><th>{t("prog.col_time")}</th><th>SKU</th><th>{t("prog.col_bonus")}</th><th></th></tr></thead>
            <tbody>
              {endedPrograms.map(p => (
                <tr key={p.id} style={editingProg?.id === p.id ? { background: "#FFF7E6" } : { opacity: 0.75 }}>
                  <td>{p.name}</td>
                  <td style={{ fontSize: 12, color: "#888" }}>{p.startDate} → {p.endDate}</td>
                  <td style={{ fontSize: 12 }}>{Array.isArray(p.skus) ? p.skus.join(", ") : p.skus}</td>
                  <td>{Number(p.amountPerUnit).toLocaleString("vi-VN")} đ</td>
                  {progActions(p)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {revokedPrograms.length > 0 && (
        <div className="card">
          <div className="card-title" style={{ color: "#A32D2D" }}>{t("prog.revoked_title")}</div>
          <table>
            <thead><tr><th>{t("prog.col_name")}</th><th>{t("prog.col_time")}</th><th>SKU</th><th>{t("prog.col_bonus")}</th><th></th></tr></thead>
            <tbody>
              {revokedPrograms.map(p => (
                <tr key={p.id} style={{ opacity: 0.7 }}>
                  <td>
                    <span style={{ textDecoration: "line-through" }}>{p.name}</span>
                    {p.revokeReason && <div style={{ fontSize: 11, color: "#A32D2D" }}>{t("prog.reason")}: {p.revokeReason}</div>}
                  </td>
                  <td style={{ fontSize: 12, color: "#888" }}>{p.startDate} → {p.endDate}</td>
                  <td style={{ fontSize: 12 }}>{Array.isArray(p.skus) ? p.skus.join(", ") : p.skus}</td>
                  <td>{Number(p.amountPerUnit).toLocaleString("vi-VN")} đ</td>
                  {progActions(p)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="card">
        <div className="card-title">{t("rule.title")}</div>
        <table>
          <thead><tr><th>{t("rule.col_type")}</th><th>{t("rule.col_cond")}</th><th>{t("rule.col_amount")}</th></tr></thead>
          <tbody>
            <tr><td>{t("rule.ds1")}</td><td>80% ≤ Hit rate &lt; 100%</td><td>4,500,000 đ</td></tr>
            <tr><td>{t("rule.ds2")}</td><td>100% ≤ Hit rate &lt; 120%</td><td>6,500,000 đ</td></tr>
            <tr><td>{t("rule.ds3")}</td><td>Hit rate ≥ 120%</td><td>9,750,000 đ</td></tr>
            <tr><td>{t("rule.he_per")}</td><td>{t("rule.he_cond")}</td><td>{t("rule.he_amount")}</td></tr>
            <tr><td>{t("rule.he_over")}</td><td>{t("rule.he_over_cond")}</td><td>{t("rule.he_over_amount")}</td></tr>
          </tbody>
        </table>
        <div style={{ fontSize: 12, color: "#888", marginTop: 8 }}>
          {t("rule.hint")}
        </div>
      </div>
    </>
  );
}
